/** The connected feeds, and the one thing about them that must not be quiet: a stop (COL-04, ADR-0123).
 *
 * **Renders nothing for a workspace with no feed.** No feed is not a healthy feed, and "all sources
 * connected" would be a claim about a thing that does not exist. A failed read is a different state
 * again (EXP-29) and says so, with a way to try again - an empty array must never be allowed to
 * look like the first.
 *
 * **A stopped feed says four things, in words**: that it is stopped and why, since when, that
 * nothing already received was lost, and where the next run resumes. The last two are the
 * requirement - *"without losing the last successful checkpoint"* - stated to the person who has to
 * decide whether to worry. The checkpoint is the server's (append-only; the highest sequence is
 * current) and is only ever described here, never edited or reset: there is no control for it.
 *
 * **Only an Admin is offered the renewal** (design rule 6). The server refuses anyone else, so a
 * button that predictably 403s would be the defect rule 6 names. Everybody else is told who can.
 * The credential field takes a *name* in the customer's own secrets store; the label says so, and
 * nothing here ever asks for, shows or stores a secret.
 */

import { useState } from "react";

import { useMe } from "../api/auth";
import type { Connector } from "../api/ingestion";
import { useConnectors, useRenewCredential } from "../api/ingestion";
import { Refused } from "../api/refusal";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { StatusPill } from "../components/StatusPill";
import { shortDate } from "../lib/format";
import styles from "./IngestionPanel.module.css";

/** Roles the server lets read this. An Auditor is not one, so no request is made for them. */
const MAY_READ = new Set(["admin", "approver", "contributor", "viewer"]);

function checkpointSentence(connector: Connector): string {
  const checkpoint = connector.checkpoint;
  if (checkpoint === null) return "Nothing has been received from it yet, so there is nothing to resume from.";
  return `The next run resumes after the last successful checkpoint (number ${checkpoint.seq}, ${shortDate(checkpoint.at)}), so nothing already received is read again or lost.`;
}

function pill(connector: Connector) {
  switch (connector.credential.state) {
    case "expired":
      return <StatusPill tone="crit">Stopped: credential expired</StatusPill>;
    case "revoked":
      return <StatusPill tone="crit">Stopped: credential withdrawn</StatusPill>;
    case "expiring":
      return (
        <StatusPill tone="warn">
          Credential expires {shortDate(connector.credential.expires_at)}
        </StatusPill>
      );
    default:
      return <StatusPill tone="ok">Connected</StatusPill>;
  }
}

function lastRunSentence(connector: Connector): string | null {
  const run = connector.last_run;
  if (!run || connector.stopped) return null;
  if (run.outcome === "source_unavailable") {
    return "The last run could not reach the source. The credential is not at fault; it will try again on the next scheduled run.";
  }
  if (run.outcome === "error") {
    return "The last run failed unexpectedly and kept nothing it had started. The checkpoint is where it was.";
  }
  if (run.outcome === "advanced") {
    return `The last run received ${run.documents_received} document${run.documents_received === 1 ? "" : "s"}${run.duplicates ? ` and passed over ${run.duplicates} it already held` : ""}. Each waits for a person to read and verify it, like an upload.`;
  }
  return null;
}

function Renew({ connector }: { connector: Connector }) {
  const renew = useRenewCredential();
  const [secretRef, setSecretRef] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const ready = secretRef.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(expiresOn);
  const id = connector.id;
  return (
    <form
      className={styles.form}
      data-testid={`renew-${id}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) renew.mutate({ connectorId: id, secretRef, expiresOn });
      }}
    >
      <label className={styles.field}>
        Credential reference
        <input
          value={secretRef}
          maxLength={200}
          onChange={(event) => setSecretRef(event.target.value)}
          aria-describedby={`renew-note-${id}`}
          autoComplete="off"
        />
      </label>
      <label className={styles.field}>
        Good until
        <input type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} />
      </label>
      <button type="submit" className={styles.renew} disabled={!ready || renew.isPending}>
        {renew.isPending ? "Renewing…" : "Renew credential"}
      </button>
      <p className={styles.note} id={`renew-note-${id}`}>
        The reference is the name of the credential in your own secrets store. Never type the secret
        itself here.
      </p>
      <WriteFailed error={renew.error} of="that credential" testId={`renew-refused-${id}`} />
    </form>
  );
}

function Feed({ connector, mayRenew }: { connector: Connector; mayRenew: boolean }) {
  const state = connector.credential.state;
  const stopped = connector.stopped;
  const tone = stopped ? styles.stopped : state === "expiring" ? styles.expiring : "";
  const since = connector.stopped_since ? shortDate(connector.stopped_since) : null;
  const ran = lastRunSentence(connector);
  return (
    <div
      className={`${styles.row} ${tone}`}
      data-testid={`connector-${connector.id}`}
      data-state={state}
      data-stopped={stopped ? "true" : "false"}
    >
      <div className={styles.head}>
        <h2 className={styles.name}>{connector.name}</h2>
        <span data-testid={`connector-state-${connector.id}`}>{pill(connector)}</span>
      </div>
      {stopped ? (
        <p className={styles.said} role="alert" data-testid={`connector-stopped-${connector.id}`}>
          Ingestion for this feed has been stopped since {since ?? "an unrecorded date"}, because its
          credential {state === "revoked" ? "was withdrawn" : "has expired"}. {checkpointSentence(connector)}
        </p>
      ) : (
        <p className={styles.said}>
          {state === "expiring"
            ? `Its credential runs out on ${shortDate(connector.credential.expires_at)}; after that, ingestion stops until it is renewed. `
            : ""}
          {connector.checkpoint
            ? `Last successful checkpoint: number ${connector.checkpoint.seq}, ${shortDate(connector.checkpoint.at)}.`
            : "Nothing has been received from it yet."}
        </p>
      )}
      {ran ? <p className={styles.meta}>{ran}</p> : null}
      {stopped || state === "expiring" ? (
        mayRenew ? (
          <Renew connector={connector} />
        ) : (
          <p className={styles.meta} data-testid={`renew-who-${connector.id}`}>
            Only an Admin can renew a credential.
          </p>
        )
      ) : null}
    </div>
  );
}

export function IngestionPanel() {
  const { data: me } = useMe();
  const allowed = me ? MAY_READ.has(me.role) : false;
  const feeds = useConnectors(allowed);
  if (!allowed) return null;
  if (feeds.isPending) return null;
  if (feeds.error) {
    // An Auditor never gets here (no request is made), so a refusal is a real one.
    if (feeds.error instanceof Refused && feeds.error.isUnauthorized) return null;
    return (
      <div className={styles.panel} data-testid="ingestion-failed">
        <ReadFailed of="The status of the connected feeds" error={feeds.error} onRetry={() => void feeds.refetch()} />
      </div>
    );
  }
  const connectors = feeds.data ?? [];
  if (connectors.length === 0) return null;
  const mayRenew = me?.role === "admin";
  return (
    <section className={styles.panel} aria-label="Connected feeds" data-testid="ingestion-status">
      {connectors.map((connector) => (
        <Feed key={connector.id} connector={connector} mayRenew={mayRenew} />
      ))}
    </section>
  );
}
